import React, { useState } from 'react';
import { Table, Button, Space, Input, Select, Alert, Tooltip } from 'antd';
import { useAppDispatch } from '@/redux/hooks';
import { closeDialog, setDialog } from '@/redux/reducers/dialog';
// import { Confirmation } from '@/components/Confirmation';
import { CreateUserForm } from './CreateUserForm';
import { useGetUsersQuery, useUpdateUserMutation } from '@/hooks/api/users';

const { Option } = Select;

interface UserTableRecord {
  key: string;
  id: string;
  name: string;
  email: string;
  role: string;
}

export const UserManagement = () => {
  const dispatch = useAppDispatch();
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<{ name: string; role: string }>({
    name: '',
    role: '',
  });

  // Fetch users from API
  const { data: usersData, isLoading, error, refetch } = useGetUsersQuery();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  const handleCreateUserSuccess = () => {
    dispatch(closeDialog());
    // Refetch users to update the table
    refetch();
  };

  // Transform API data to table format
  const tableData =
    usersData?.users?.map((user) => ({
      key: user.user_id,
      id: user.user_id,
      name: user.name,
      email: user.email,
      role: user.superuser === 'true' ? 'Admin' : 'Read Only', // Adjust role display
    })) || [];

  const handleOpenCreateUserDialog = () => {
    dispatch(
      setDialog({
        options: { className: 'rounded-[15px] w-[800px]' },
        content: (
          <CreateUserForm
            onSuccess={handleCreateUserSuccess}
            onCancel={() => dispatch(closeDialog())}
          />
        ),
        fixedWidth: 800,
      }),
    );
  };

  const handleEditUser = (userId: string, name: string, role: string) => {
    setEditingUserId(userId);
    setEditingData({ name, role });
  };

  const handleSaveEdit = async (userId: string) => {
    try {
      await updateUser({ userId, body: editingData }).unwrap();
      setEditingUserId(null);
      setEditingData({ name: '', role: '' });
      refetch();
    } catch (err) {
      console.error('Failed to update user:', err);
    }
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditingData({ name: '', role: '' });
  };

  // const handleConfirmDelete = (userId: string) => {
  //   dispatch(
  //     setDialog({
  //       options: { className: 'rounded-[15px] w-[400px]' },
  //       content: (
  //         <Confirmation
  //           text={'Are you sure you want to delete this user?'}
  //           onConfirm={() => {
  //             dispatch(closeDialog());
  //           }}
  //           onClose={() => dispatch(closeDialog())}
  //         />
  //       ),
  //       fixedWidth: 400,
  //     }),
  //   );
  // };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a || !b) return 0;
        return (a.name || '').localeCompare(b.name || '');
      },
      render: (text: string, record: UserTableRecord) => {
        if (editingUserId === record.key) {
          return (
            <Input
              value={editingData.name}
              onChange={(e) => setEditingData({ ...editingData, name: e.target.value })}
              placeholder="Enter name"
            />
          );
        }
        return text;
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a || !b) return 0;
        return (a.email || '').localeCompare(b.email || '');
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a || !b) return 0;
        return (a.role || '').localeCompare(b.role || '');
      },
      render: (text: string, record: UserTableRecord) => {
        if (editingUserId === record.key) {
          return (
            <Select
              value={editingData.role}
              onChange={(value) => setEditingData({ ...editingData, role: value })}
              placeholder="Select a role"
              style={{ width: '100%' }}
            >
              <Option value="view only">View Only</Option>
              <Option value="admin">Admin</Option>
            </Select>
          );
        }
        return text;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: UserTableRecord) => {
        if (editingUserId === record.key) {
          return (
            <Space size="middle">
              <Button type="link" onClick={() => handleSaveEdit(record.key)} loading={isUpdating}>
                Save
              </Button>
              <Button type="link" onClick={handleCancelEdit}>
                Cancel
              </Button>
            </Space>
          );
        }
        return (
          <Space size="middle">
            <Tooltip title="Feature will be added soon.">
              <Button
                type="link"
                onClick={() => handleEditUser(record.key, record.name, record.role)}
                disabled
              >
                Edit
              </Button>
            </Tooltip>
            <Tooltip title="Feature will be added soon.">
              <Button
                type="link"
                danger
                // onClick={() => handleConfirmDelete(record.key)}
                disabled
              >
                Delete
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="bg-gray-50 w-full">
      <div className="py-6 px-6 m-6 bg-white rounded flex justify-between items-center">
        <h1 className="text-xl font-semibold">User Management</h1>
        <Tooltip title="Feature will be added soon.">
          <Button
            type="primary"
            className="bg-black text-white hover:bg-gray-200"
            onClick={handleOpenCreateUserDialog}
            disabled
          >
            Create New User
          </Button>
        </Tooltip>
      </div>
      <div className="p-6 m-6 bg-white rounded">
        {error && (
          <Alert
            message="Error loading users"
            description="Failed to fetch users from the server. Please try again."
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        )}
        <Table
          columns={columns}
          dataSource={tableData}
          loading={isLoading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
          }}
        />
      </div>
    </div>
  );
};

export default UserManagement;
